import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Field, useFormikContext } from "formik";
import cn from "clsx";
import { Tooltip } from "flowbite-react";
import SearchableSelect from "../SearchableSelect/SearchableSelect";
import { DynamicPositioningPortal } from "../DynamicPositioning/DynamicPositioning";
import StarRatingInp from "../../shared/Star/Star";
import { twMerge } from "tailwind-merge";
import { debounce } from "../common";

const Input = (props) => {
  const {
    name,
    label,
    type = "text",
    required,
    disabled = false,
    errors,
    touched,
    hideLabel,
    theme = {},
    width,
    values,
    CustomComponent,
    handleChange,
    ...rest
  } = props;
  const exactName = name.split(".").slice(-1)[0];
  const inputRef = useRef();
  const { setFieldValue } = useFormikContext();
  const {
    container = "",
    labelWrapper = "",
    labelClass = "",
    fieldWrapper = "",
    fieldClass = "",
    errorWrapper = "",
  } = theme;
  return (
    <div
      className={twMerge(
        cn("flex flex-col justify-start items-start mr-4", container)
      )}
      style={{ width: width + "%" }}
    >
      <div className={cn("grow ml-1", labelWrapper)}>
        {
          <label className={labelClass} htmlFor={name}>
            {label}
          </label>
        }
        {required && <span className="text-red-600">*</span>}
      </div>
      <div className={cn("relative", fieldWrapper)}>
        <Field
          type={type}
          {...(handleChange && {
            onChange: (event) => {
              handleChange(event, setFieldValue);
            },
          })}
          innerRef={inputRef}
          name={name}
          {...rest}
          className={cn(
            "border-[#d1d1d1] border-2 focus:ring-0 focus:outline-none rounded-[4px] mt-1 w-full  text-sm font-normal",
            {
              ["border-red-600"]: errors?.[exactName] && touched?.[exactName],
            },
            fieldClass,
            disabled && "pointer-events-none !bg-[#F2F2F2]"
          )}
          disabled={disabled}
        />
        {CustomComponent && CustomComponent(inputRef)}
      </div>
      {errors?.[exactName] && touched?.[exactName] && (
        <span className={cn("text-red-600 text-sm", errorWrapper)}>
          {errors?.[exactName]}
        </span>
      )}
    </div>
  );
};

const TextArea = (props) => {
  const { label, name, required, errors, touched, width, values, ...rest } =
    props;
  const exactName = name.split(".").slice(-1)[0];
  const inputRef = useRef();
  return (
    <div
      className={cn("flex flex-col justify-start items-start mr-4")}
      style={{ width: width + "%" }}
    >
      <div className="grow ml-1">
        <label htmlFor={name}>{label}</label>
        {required && <span className="text-red-600">*</span>}
      </div>
      <Field
        as="textarea"
        id={name}
        name={name}
        {...rest}
        className={cn(
          "border-[#d1d1d1] border-2 focus:ring-0 focus:outline-none rounded-[4px] mt-1 w-full placeholder:text-[#d1d1d1] disabled:cursor-not-allowed disabled:!bg-[#F2F2F2] text-sm font-normal",
          {
            ["border-red-600"]: errors?.[exactName] && touched?.[exactName],
          }
        )}
      />
      {errors?.[exactName] && touched?.[exactName] && (
        <span className="text-red-600 text-sm">{errors?.[exactName]}</span>
      )}
    </div>
  );
};

const Select = (props) => {
  const {
    options,
    label,
    required,
    name,
    handleChange,
    disabled,
    getAsyncOptions,
    defaultSearchText = "",
    width,
    callOnEmptyText = false,
    theme = {},
    styleClass = "",
    errors,
    touched,
    values,
    indexOfField,
    labelCom,
    handleInfiniteScroll,
    fetchOnKeyChange,
  } = props;

  const [activePage, setActivePage] = useState(1);
  const exactName = name.split(".").slice(-1)[0];
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [searchText, setSearchText] = useState(defaultSearchText);
  const inputRef = useRef();
  const inputBlinkerRef = useRef();
  const {
    container = "",
    labelWrapper = "",
    labelClass = "",
    fieldClass = "",
    // Additional classNames can be added here as needed
  } = theme;

  useEffect(() => {
    getAsyncOptions &&
      (searchText || callOnEmptyText) &&
      getAsyncOptions(searchText, setIsFetchingOptions, setHasMorePages);
    setActivePage(1);
  }, [searchText, values?.[fetchOnKeyChange]]);

  useEffect(() => {
    const outsideClickHandler = (e) => {
      const selectContainer = document.getElementById(
        "select-container-" + name
      );
      if (!(selectContainer && selectContainer.contains(e.target))) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", outsideClickHandler);
    return () => {
      document.removeEventListener("click", outsideClickHandler);
    };
  }, []);

  useEffect(() => {
    if (inputBlinkerRef?.current) {
      inputBlinkerRef?.current.scrollBy({
        left: 1000,
        behaviour: "smooth",
      });
    }
  }, [searchText]);

  useEffect(() => {
    if (!isOpen) setActivePage(1);
  }, [isOpen]);

  useEffect(() => {
    if (defaultSearchText === searchText && options?.length) {
      setSearchText("");
    }
  }, [defaultSearchText, options]);

  const containerRef = useRef();

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      !isFetchingOptions &&
        handleInfiniteScroll &&
        handleInfiniteScroll(
          searchText,
          activePage,
          setActivePage,
          setIsFetchingOptions,
          setHasMorePages
        );
    }
  };

  return (
    <div
      id={"select-container-" + name}
      className={`flex flex-col justify-start items-start mr-4 ${container}`}
      style={{ width: width + "%" }}
    >
      <div className={cn("grow ml-1 flex", labelWrapper)}>
        <label className={cn("flex gap-1", labelClass)} htmlFor={name}>
          {labelCom ? (
            <>
              <span>{label}</span>
              <div className="whitespace-pre-wrap">
                <Tooltip content={labelCom} style="dark" placement="bottom">
                  <button>
                    <i className="fa-solid fa-circle-info text-sm"></i>
                  </button>
                </Tooltip>
              </div>
            </>
          ) : (
            label
          )}
        </label>
        {required && label && <span className="text-red-600">*</span>}
      </div>
      <div className="flex"></div>
      <Field name={name}>
        {({ form }) => {
          const { setFieldValue, setFieldTouched, touched: allTouched } = form;
          const selectedValueLabel = values?.[exactName]?.label || "";
          return (
            <div
              className={`relative inline-block text-left w-full ${styleClass}`}
            >
              <button
                type="button"
                ref={containerRef}
                className={cn(
                  "flex justify-center w-full rounded-[4px] border-2 border-[#d1d1d1] pr-2 pl-1 py-2 bg-white focus:ring-0 focus:outline-none mt-1",
                  {
                    ["pointer-events-none !bg-[#F2F2F2]"]: disabled,
                    ["border-red-600"]:
                      errors?.[exactName] &&
                      (touched?.[exactName] || allTouched?.[name]),
                  },
                  fieldClass
                )}
                id="options-menu"
                onClick={(e) => {
                  setIsOpen(!isOpen);
                  setFieldTouched([name], true);
                  inputRef?.current && inputRef.current.focus();
                }}
              >
                {getAsyncOptions && (
                  <div
                    className="before:content-[attr(data-value)] before:flex before:contentEditable before:shrink-0 before:flex-wrap flex items-center h-6 overflow-x-auto hide-scrollbar font-normal text-sm"
                    data-value={searchText}
                    ref={inputBlinkerRef}
                  >
                    {isInputFocused && !disabled && (
                      <div
                        className="w-1 h-4 bg-black flex flex-shrink-0 "
                        style={{
                          animation: "cursor-blink 0.9s steps(2) infinite",
                          borderLeft: "2px solid white",
                        }}
                      ></div>
                    )}
                    {!Boolean(searchText) && !selectedValueLabel && (
                      <div className="text-[#d1d1d1]">
                        {isFetchingOptions ? "Loading..." : "Search..."}
                      </div>
                    )}
                    <input
                      className="absolute -left-[1500px]"
                      ref={inputRef}
                      type="text"
                      onFocus={() => setIsInputFocused(true)}
                      onBlur={() => setIsInputFocused(false)}
                      onChange={debounce((e) => {
                        !isOpen && setIsOpen(true);
                        setSearchText(e.target.value.trim());
                      }, 500)}
                    />
                  </div>
                )}
                {!getAsyncOptions && !values?.[exactName] && (
                  <div className="text-[#d1d1d1]">Select</div>
                )}
                {!searchText && values?.[exactName] && (
                  <Tooltip
                    content={selectedValueLabel}
                    theme={{ target: "w-5/6" }}
                  >
                    <div className="block truncate font-normal text-sm text-left flex-wrap">
                      {selectedValueLabel}
                    </div>
                  </Tooltip>
                )}
                <svg
                  className="-mr-1 h-5 w-5 ml-auto flex flex-shrink-0 color-[#d1d1d1]"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="#d1d1d1"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.707a1 1 0 011.414 0L10 11.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {isOpen && (
                <DynamicPositioningPortal parentRef={containerRef.current}>
                  <ul
                    className="origin-top-right font-normal absolute right-0 mt-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 max-h-[190px] scrollbar-thin scrollbar-w-1 scrollbar-thumb-primary scrollbar-track-rounded-lg scrollbar-thumb-rounded-full scrollbar-track-[#d1d1d1] overflow-hidden"
                    aria-orientation="vertical"
                    aria-labelledby="options-menu"
                    {...(hasMorePages ? { onScroll: handleScroll } : {})}
                  >
                    {(isFetchingOptions && activePage === 1 && !!searchText) ||
                    options.length === 0 ? (
                      <li className="block px-4 py-2 text-sm text-gray-300">
                        {isFetchingOptions ? "Loading..." : "No Option"}
                      </li>
                    ) : (
                      (options || []).map((option, index) => {
                        return (
                          <li
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer"
                            key={index}
                            onClick={() => {
                              handleChange &&
                                handleChange(
                                  option.value,
                                  setFieldValue,
                                  indexOfField,
                                  option
                                );
                              const splittedArrary = name.split(".");
                              const transformedName =
                                splittedArrary.length > 1
                                  ? `${splittedArrary[0]}[${splittedArrary[1]}].${splittedArrary[2]}`
                                  : name;
                              setFieldValue(transformedName, option);
                              setIsOpen(false);
                              setSearchText("");
                            }}
                          >
                            {option.label}
                          </li>
                        );
                      })
                    )}
                  </ul>
                </DynamicPositioningPortal>
              )}
              {errors?.[exactName] &&
                (touched?.[exactName] ||
                  touched?.[name] ||
                  allTouched?.[name]) && (
                  <span className="text-red-600 text-xs">
                    {errors?.[exactName]}
                  </span>
                )}
            </div>
          );
        }}
      </Field>
    </div>
  );
};

function RadioButtons(props) {
  const { label, name, options, ...rest } = props;
  return (
    <div>
      <label>{label}</label>
      <Field name={name}>
        {(formik) => {
          const { field } = formik;
          return options.map((option) => {
            return (
              <div key={option.key}>
                <input
                  type="radio"
                  id={option.value}
                  {...field}
                  {...rest}
                  value={option.value}
                  checked={field.value === option.value}
                />
                <label htmlFor={option.value}>{option.key}</label>
              </div>
            );
          });
        }}
      </Field>
      <ErrorMessage name={name} />
    </div>
  );
}

const Checkbox = ({
  label,
  control,
  name,
  inputStyle = "",
  parentStyle = "",
  handleChange = () => {},
}) => {
  const { setFieldValue } = useFormikContext();
  return (
    <div className={parentStyle}>
      <Field
        onChange={(event) => {
          setFieldValue(name, event.target.checked);
          handleChange(event.target.checked, setFieldValue);
        }}
        className={inputStyle}
        name={name}
        type={control}
      />
      <label>{label}</label>
    </div>
  );
};

const StarRating = ({
  name,
  label,
  totalStars,
  required,
  width,
  errors,
  touched,
  disabled,
}) => {
  //  sample FormConfig={
  //   control: "ratingInput",
  //   label: "Rating",
  //   name: "ratingInput",
  //   totalStars: 5,
  //   required: true,
  //   width: 15,
  //   disabled:false
  // }
  // Sample Validation Schema
  // ratingInput: Yup.number()
  // .min(1, "Number must be greater than zero")
  // .required("Number is required"),
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const handleStarClick = (value) => {
    setFieldValue(name, value);
  };

  return (
    <div
      className={cn("flex flex-col justify-start items-start", {
        "cursor-not-allowed": disabled,
      })}
      style={{ width: width + "%" }}
    >
      <div className="flex items-center w-full mb-3">
        <label htmlFor={name}>{label}</label>
        {required && label && <span className="text-red-600">*</span>}
      </div>
      <Field
        name={name}
        as={StarRatingInp}
        totalStars={totalStars}
        onStarClick={handleStarClick}
        handleBlur={() => setFieldTouched(name)}
        error={touched?.[name] && errors?.[name]}
        disabled={disabled}
      />
    </div>
  );
};

const MultiSelect = (props) => {
  const {
    options,
    name,
    label,
    errors,
    touched,
    maxLimit,
    required,
    values,
    theme = {},
    width,
    isMulti = true,
    isCreatable,
    handleChange,
    ...rest
  } = props;
  const {
    container = "",
    labelClass = "",
    fieldClass = "",
    searchField = "",
    // Additional classNames can be added here as needed
  } = theme;
  const exactName = name.split(".").slice(-1)[0];
  return (
    <Field name={name}>
      {({ field, form: { setFieldValue, setFieldTouched } }) => (
        <div className={cn("mr-2", container)} style={{ width: width + "%" }}>
          <label className={labelClass}> {label}</label>
          {required && label && <span className="text-red-600">*</span>}
          <SearchableSelect
            className={fieldClass}
            searchField={searchField}
            maxLimit={maxLimit}
            id={name}
            {...field}
            {...rest}
            options={options}
            isMulti={isMulti}
            creatable={isCreatable}
            values={values?.[exactName] || []}
            handleChange={(impactedValue, type, selectedValues) => {
              if (maxLimit && selectedValues.length > maxLimit) {
                return;
              }
              !!handleChange &&
                handleChange(
                  selectedValues,
                  setFieldValue,
                  type,
                  impactedValue
                );
              setFieldValue(name, selectedValues);
            }}
            onBlur={() => setFieldTouched(name, true)}
          />
          {errors?.[exactName] && touched?.[exactName] && (
            <span className="text-red-600 text-xs">{errors?.[exactName]}</span>
          )}
        </div>
      )}
    </Field>
  );
};

function FormikController(props) {
  const { control, ...rest } = props;
  switch (control) {
    case "input":
      return <Input {...rest} />;
    case "textarea":
      return <TextArea {...rest} />;
    case "seachableselect":
      return <SearchableSelect />;
    case "select":
      return <Select {...rest} />;
    case "radio":
      return <RadioButtons {...rest} />;
    case "checkbox":
      return <Checkbox {...props} />;
    case "multiselect":
      return <MultiSelect {...props} />;
    case "ratingInput":
      return <StarRating {...rest} />;
    default:
      return null;
  }
}
export default FormikController;
